import React, { memo, useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { onSnapshot, doc } from 'firebase/firestore';
import { useAccount } from '../contexts/AccountContext';
import { useAccountType } from '../contexts/AccountTypeContext';
import { useTheme } from '../contexts/ThemeContext';
import { useFullChartView } from '../contexts/FullChartViewContext';
import { useOptimizedDropdown } from '../hooks/useOptimizedDropdown';
import { useResponsiveHeader } from '../hooks/useResponsiveHeader';
import { useAuth } from '../contexts/AuthContext';
import { usePositionContext } from '../contexts/PositionContext';
import { UserDocumentService } from '../services/userDocumentService';
import { db } from '../config/firebase';
import DepositModal from './DepositModal';

const HeaderMiddlePanel: React.FC = memo(() => {
  const { getAccountBalance, refillDemoAccount, balanceUpdateTrigger, getSimplifiedBalance, resetLiveAccountBalance } = useAccount();
  const { isDemoAccount, toggleAccountType, accountType } = useAccountType();
  const { user } = useAuth();
  const { openPositions } = usePositionContext();
  
  // Get account-specific balance data
  const accountBalance = getAccountBalance(accountType);
  const { theme } = useTheme();
  const { isFullChartView } = useFullChartView();
  
  // State for deposit modal
  const [showDepositModal, setShowDepositModal] = useState(false);
  
  // Define the account summary type
  type AccountSummaryType = {
    balance: {
      totalBalance: number;
      availableBalance: number;
      lockedBalance: number;
      currency: string;
    };
    margin: {
      freeMargin: number;
      usedMargin: number;
      marginLevel: number;
      marginCallLevel: number;
      stopOutLevel: number;
    };
    equity: {
      totalEquity: number;
      freeEquity: number;
      usedEquity: number;
    };
    pnl: {
      realizedPnL: number;
      unrealizedPnL: number;
      totalPnL: number;
      dailyPnL: number;
      weeklyPnL: number;
      monthlyPnL: number;
      yearlyPnL: number;
    };
  };

  // State for account summary - load directly from Firestore
  const [accountSummary, setAccountSummary] = useState<AccountSummaryType | null>(null);
  
  // Load account summary with local storage caching
  const loadAccountSummary = useCallback(async () => {
    try {
      
      if (!user) {
        return;
      }

      const storageKey = `accountSummary_${user.uid}_${accountType}`;
      
      // Skip localStorage cache and always fetch fresh data from Firestore
      // This ensures we always show the most current trading data
      
      // Fetch from Firestore and update both state and local storage
      const userDoc = await UserDocumentService.getUserDocument(user.uid);
      const firestoreAccountType = accountType === 'live' ? 'real' : 'demo';
      const accountData = userDoc?.accounts?.[firestoreAccountType];
      
      
      if (accountData?.accountSummary) {
        
        const freshData = {
          balance: accountData.accountSummary.balance,
          margin: accountData.accountSummary.margin,
          equity: accountData.accountSummary.equity,
          pnl: accountData.accountSummary.pnl,
          leverage: accountData.accountSummary.leverage,
          deposits: accountData.accountSummary.deposits,
          withdrawals: accountData.accountSummary.withdrawals,
          statistics: accountData.accountSummary.statistics
        };
        
        setAccountSummary(freshData);
        
        // Update local storage with fresh data
        localStorage.setItem(storageKey, JSON.stringify(freshData));
        
      } else {
      }
    } catch (error) {
      console.error('❌ Error loading accountSummary:', error);
    }
  }, [accountType, user]);

  // Load account summary on mount and when dependencies change
  useEffect(() => {
    // Clear localStorage cache to ensure fresh data
    if (user) {
      const storageKey = `accountSummary_${user.uid}_${accountType}`;
      localStorage.removeItem(storageKey);
    }
    loadAccountSummary();
  }, [loadAccountSummary, balanceUpdateTrigger, user, accountType]);

  // Real-time Firestore listener for immediate updates
  useEffect(() => {
    if (!user) return;

    const userDocRef = doc(db, 'userDocuments', user.uid);
    const firestoreAccountType = accountType === 'live' ? 'real' : 'demo';
    
    
    const unsubscribe = onSnapshot(userDocRef, (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        const accountData = userData.accounts?.[firestoreAccountType];
        
        if (accountData?.accountSummary) {
          
          const freshData = {
            balance: accountData.accountSummary.balance,
            margin: accountData.accountSummary.margin,
            equity: accountData.accountSummary.equity,
            pnl: accountData.accountSummary.pnl,
            leverage: accountData.accountSummary.leverage,
            deposits: accountData.accountSummary.deposits,
            withdrawals: accountData.accountSummary.withdrawals,
            statistics: accountData.accountSummary.statistics
          };
          
          setAccountSummary(freshData);
        }
      }
    }, (error) => {
      console.error('❌ Real-time listener error:', error);
    });

    return () => {
      unsubscribe();
    };
  }, [user, accountType]);

  // Additional immediate refresh when account type changes (switching between demo/live)
  useEffect(() => {
    loadAccountSummary();
  }, [accountType, loadAccountSummary]);

  // Subscribe to SimplifiedBalanceService updates to refresh from Firestore
  useEffect(() => {
    const { SimplifiedBalanceService } = require('../services/SimplifiedBalanceService');
    
    const unsubscribe = SimplifiedBalanceService.onBalanceUpdate(() => {
      loadAccountSummary();
    });
    
    return unsubscribe;
  }, [loadAccountSummary]);

  // Auto-reset Live account balance if it's incorrectly set to $1000
  useEffect(() => {
    if (user && accountType === 'live' && accountBalance.totalBalance === 1000) {
      // console.log('🔄 Detected Live account with incorrect $1000 balance, resetting to $0.00...');
      resetLiveAccountBalance();
    }
  }, [user, accountType, accountBalance.totalBalance, resetLiveAccountBalance]);

  // Subscribe to position updates to refresh account summary immediately
  useEffect(() => {
    const { positionEventEmitter } = require('../services/positionEventEmitter');
    
    const handlePositionUpdate = () => {
      // Add a small delay to ensure Firestore has been updated
      setTimeout(() => {
        loadAccountSummary();
      }, 1000);
    };
    
    positionEventEmitter.on('positionsUpdated', handlePositionUpdate);
    
    return () => {
      positionEventEmitter.off('positionsUpdated', handlePositionUpdate);
    };
  }, [loadAccountSummary]);

  // Subscribe to balance update triggers to refresh immediately when balance changes
  useEffect(() => {
    if (balanceUpdateTrigger > 0) {
      // Add a small delay to ensure Firestore has been updated
      setTimeout(() => {
        loadAccountSummary();
      }, 2000);
    }
  }, [balanceUpdateTrigger, loadAccountSummary]);

  // Debug accountSummary state changes
  useEffect(() => {
  }, [accountSummary]);

  // Refill demo account function
  const handleRefillDemo = async () => {
    try {
      await refillDemoAccount();
    } catch (error) {
      console.error('❌ Error refilling demo account:', error);
    }
  };
  
  // Memoize balance values to prevent infinite re-renders
  const balanceValues = useMemo(() => {
    return {
      totalBalance: accountBalance.totalBalance,
      freeMargin: accountBalance.freeMargin,
      margin: accountBalance.margin,
      marginLevel: accountBalance.marginLevel,
      profit: accountBalance.profit, // This should be realized profit from closed positions only
      equity: accountBalance.equity,
    };
  }, [accountBalance, balanceUpdateTrigger]);

  // The account's overall P&L: what closed trades have banked plus what open ones are worth
  // right now. It used to show `realizedPnL` alone, which meant a running trade moved equity
  // but left this figure sitting still until the trade was closed.
  //
  // `totalPnL` is written alongside the other two, but older documents predate it, so it is
  // recomputed here rather than trusted.
  const totalAccountPnL = useMemo(() => {
    if (!accountSummary) return balanceValues.profit;

    const realized = accountSummary.pnl?.realizedPnL ?? 0;
    const unrealized = accountSummary.pnl?.unrealizedPnL ?? 0;
    return realized + unrealized;
  }, [accountSummary, balanceValues.profit]);

  // Track balance changes and force re-renders
  useEffect(() => {
  }, [balanceUpdateTrigger, accountBalance.totalBalance, accountType]);
  
  // Container ref for responsive calculations
  const containerRef = useRef<HTMLDivElement>(null);
  const accountToggleRef = useRef<HTMLDivElement>(null);
  
  // Use optimized dropdown hook
  const { isOpen: isDropdownOpen, toggle: toggleDropdown, dropdownRef, buttonRef } = useOptimizedDropdown();

  // Define header items with their components and priorities
  // Items with alwaysInDropdown: true will always be in the dropdown by default
  const headerItems = useMemo(() => [
    {
      id: 'balance',
      priority: 1,
      alwaysInDropdown: false,
      component: (
        <div className="flex items-center space-x-1 lg:space-x-2 flex-shrink-0">
          <span className="theme-text-quaternary text-xs hidden sm:inline">Balance:</span>
          {accountSummary && accountSummary.balance.totalBalance === 0 ? (
            // Show action button when balance is $0.00
            <button
              onClick={() => {
                if (isDemoAccount) {
                  handleRefillDemo();
                } else {
                  setShowDepositModal(true);
                }
              }}
              className={`text-xs font-medium px-3 py-1.5 rounded-md transition-all duration-200 shadow-sm hover:shadow-md ${
                theme === 'light' 
                  ? 'bg-[#2AB7A6] text-white hover:bg-[#25a595] border border-[#2AB7A6]' 
                  : 'bg-[#2AB7A6] text-white hover:bg-[#25a595] border border-[#2AB7A6]'
              }`}
              title={isDemoAccount ? 'Refill demo account with $1000' : 'Deposit funds to real account'}
            >
              {isDemoAccount ? 'Refill' : 'Deposit'}
            </button>
          ) : (
            // Show Balance - available balance from Firestore
            <span className="font-medium theme-text-primary text-xs">
              {accountSummary ? (
                accountSummary.balance.availableBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              ) : (
                <span className="text-gray-400">0.00</span>
              )}
              <sup className="theme-text-quaternary ml-1" style={{ fontSize: '0.5rem' }}>USD</sup>
            </span>
          )}
        </div>
      )
    },
    {
      id: 'margin',
      priority: 2,
      alwaysInDropdown: false,
      component: (
        <div className="flex items-center space-x-1 lg:space-x-2 flex-shrink-0">
          <span className="theme-text-quaternary text-xs hidden sm:inline">Margin:</span>
          <span className="font-medium theme-text-primary text-xs">
            {accountSummary ? (
              accountSummary.margin.usedMargin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            ) : '0.00'}
            <sup className="theme-text-quaternary ml-1" style={{ fontSize: '0.5rem' }}>USD</sup>
          </span>
        </div>
      )
    },
    {
      id: 'equity',
      priority: 3,
      alwaysInDropdown: false, // Move to main header
      component: (
        <div className="flex items-center space-x-1 lg:space-x-2 flex-shrink-0">
          <span className="theme-text-quaternary text-xs hidden sm:inline">Equity:</span>
          <span className="font-medium theme-text-primary text-xs">
            {accountSummary ? (
              accountSummary.equity.totalEquity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            ) : '0.00'}
            <sup className="theme-text-quaternary ml-1" style={{ fontSize: '0.5rem' }}>USD</sup>
          </span>
        </div>
      )
    },
    {
      id: 'freeMargin',
      priority: 4,
      alwaysInDropdown: true, // Move to dropdown
      component: (
        <div className="flex items-center space-x-1 lg:space-x-2 flex-shrink-0">
          <span className="theme-text-quaternary text-xs hidden sm:inline">Free Margin:</span>
          <span className="font-medium theme-text-primary text-xs">
            {accountSummary ? (
              accountSummary.margin.freeMargin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            ) : '0.00'}
            <sup className="theme-text-quaternary ml-1" style={{ fontSize: '0.5rem' }}>USD</sup>
          </span>
        </div>
      )
    },
    {
      id: 'marginLevel',
      priority: 5,
      alwaysInDropdown: true, // Move to dropdown
      component: (
        <div className="flex items-center space-x-1 lg:space-x-2 flex-shrink-0">
          <span className="theme-text-quaternary text-xs hidden sm:inline">Margin Level:</span>
          <span className="font-medium theme-text-primary text-xs">
            {accountSummary ? (
              accountSummary.margin.marginLevel.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            ) : '0.00'}%
          </span>
        </div>
      )
    },
    {
      id: 'profit',
      priority: 6,
      alwaysInDropdown: true, // Keep in dropdown since we'll show it next to Account
      component: (
        <div className="flex items-center space-x-1 lg:space-x-2 flex-shrink-0">
          <span className="theme-text-quaternary text-xs hidden sm:inline">P&L:</span>
          {/* P&L shows realized profit/loss from closed positions only */}
          <span className={`font-medium text-xs ${accountSummary ? (accountSummary.pnl.realizedPnL >= 0 ? 'text-[#2AB7A6]' : 'text-red-400') : 'text-gray-400'}`}>
            {accountSummary ? (
              <>
                {accountSummary.pnl.realizedPnL >= 0 ? '+' : ''}{accountSummary.pnl.realizedPnL.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                <sup className="theme-text-quaternary ml-1" style={{ fontSize: '0.5rem' }}>USD</sup>
              </>
            ) : '0.00'}
          </span>
        </div>
      )
    },
    {
      id: 'leverage',
      priority: 7,
      alwaysInDropdown: true, // Always in dropdown
      component: (
        <div className="flex items-center space-x-1 lg:space-x-2 flex-shrink-0">
          <span className="theme-text-quaternary text-xs hidden sm:inline">Leverage:</span>
          <span className="font-medium theme-text-primary text-xs">
            {accountBalance.leverage}
          </span>
        </div>
      )
    }
  ], [balanceValues, isDemoAccount]);

  // Use responsive header hook when full chart view is enabled
  const { visibleItems, hiddenItems, showDropdown: responsiveShowDropdown } = useResponsiveHeader({
    items: headerItems,
    containerRef,
    accountToggleRef,
    minItemWidth: 80,
    spacing: 4
  });

  // Use responsive behavior only when full chart view is enabled
  const shouldUseResponsive = isFullChartView;

  return (
    <div ref={containerRef} className="flex-1 flex items-center justify-between px-2 min-w-0">
      {/* Left Side - Responsive Header Items */}
      <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-4 xl:space-x-6 text-xs min-w-0 flex-shrink">
        {shouldUseResponsive ? (
          <>
            {/* Render visible items directly when using responsive behavior */}
            {visibleItems.map((item) => (
              <React.Fragment key={item.id}>
                {item.component}
              </React.Fragment>
            ))}
            
            {/* Three Dots Dropdown - Only show when there are hidden items */}
            {responsiveShowDropdown && (
              <div className="relative flex-shrink-0">
                <button
                  ref={buttonRef}
                  onClick={toggleDropdown}
                  className="flex items-center justify-center w-6 h-6 rounded-full hover:theme-bg-quaternary transition-colors duration-200"
                  aria-label="More options"
                >
                  <svg
                    className="w-4 h-4 theme-text-quaternary"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                  </svg>
                </button>
                
                {/* Dropdown Menu - Show hidden items */}
                {isDropdownOpen && hiddenItems.length > 0 && (
                  <div ref={dropdownRef} className="absolute top-full right-0 mt-1 w-48 theme-bg-secondary rounded-md shadow-lg theme-border border z-[10001] max-h-64 overflow-y-auto">
                    <div className="py-1">
                      {hiddenItems.map((item) => (
                        <div key={item.id} className="px-4 py-2 text-xs hover:theme-bg-quaternary transition-colors duration-150">
                          {item.component}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Original layout when not using responsive behavior */}
            {/* Balance - Always visible */}
            <div className="flex items-center space-x-1 lg:space-x-2 flex-shrink-0">
              <span className="theme-text-quaternary text-xs">Balance:</span>
              <span className="font-medium theme-text-primary text-xs">
                {accountSummary ? (
                  accountSummary.balance.availableBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                ) : (
                  accountBalance.totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                )}
                <sup className="theme-text-quaternary ml-1" style={{ fontSize: '0.5rem' }}>USD</sup>
              </span>
            </div>
            
            {/* Margin - Always visible */}
            <div className="flex items-center space-x-1 lg:space-x-2 flex-shrink-0">
              <span className="theme-text-quaternary text-xs">Margin:</span>
              <span className="font-medium theme-text-primary text-xs">
                {accountSummary ? (
                  accountSummary.margin.usedMargin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                ) : balanceValues.margin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                <sup className="theme-text-quaternary ml-1" style={{ fontSize: '0.5rem' }}>USD</sup>
              </span>
            </div>
            
            {/* Equity - Always visible */}
            <div className="flex items-center space-x-1 lg:space-x-2 flex-shrink-0">
              <span className="theme-text-quaternary text-xs">Equity:</span>
              <span className="font-medium theme-text-primary text-xs">
                {accountSummary ? (
                  accountSummary.equity.totalEquity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                ) : accountBalance.equity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                <sup className="theme-text-quaternary ml-1" style={{ fontSize: '0.5rem' }}>USD</sup>
              </span>
            </div>
            
            {/* Three Dots Dropdown */}
            <div className="relative flex-shrink-0">
              <button
                ref={buttonRef}
                onClick={toggleDropdown}
                className="flex items-center justify-center w-6 h-6 rounded-full hover:theme-bg-quaternary transition-colors duration-200"
                aria-label="More options"
              >
                <svg
                  className="w-4 h-4 theme-text-quaternary"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
              </button>
              
              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div ref={dropdownRef} className="absolute top-full right-0 mt-1 w-48 theme-bg-secondary rounded-md shadow-lg theme-border border z-[10001] max-h-64 overflow-y-auto">
                  <div className="py-1">
                    {/* Free Margin */}
                    <div className="px-4 py-2 text-xs hover:theme-bg-quaternary transition-colors duration-150">
                      <div className="flex justify-between items-center">
                        <span className="theme-text-quaternary">Free Margin:</span>
                        <span className="font-medium theme-text-primary">
                          {accountSummary ? (
                            accountSummary.margin.freeMargin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          ) : balanceValues.freeMargin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                    
                    {/* Margin Level */}
                    <div className="px-4 py-2 text-xs hover:theme-bg-quaternary transition-colors duration-150">
                      <div className="flex justify-between items-center">
                        <span className="theme-text-quaternary">Margin Level:</span>
                        <span className="font-medium theme-text-primary">
                          {accountSummary ? (
                            accountSummary.margin.marginLevel.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          ) : balanceValues.marginLevel.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                        </span>
                      </div>
                    </div>
                    
                    {/* Leverage */}
                    <div className="px-4 py-2 text-xs hover:theme-bg-quaternary transition-colors duration-150">
                      <div className="flex justify-between items-center">
                        <span className="theme-text-quaternary">Leverage:</span>
                        <span className="font-medium theme-text-primary">
                          {accountBalance.leverage}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Right Side - P&L and Account Type Toggle */}
      <div ref={accountToggleRef} className="flex items-center space-x-1 lg:space-x-2 flex-shrink-0">
        {/* P&L - Directly left of Account toggle */}
        <div className="flex items-center space-x-1 lg:space-x-2 flex-shrink-0">
          <span className="theme-text-quaternary text-xs">P&L:</span>
          <span className={`font-medium text-xs ${totalAccountPnL >= 0 ? 'text-[#2AB7A6]' : 'text-red-400'}`}>
            {totalAccountPnL >= 0 ? '+' : ''}
            {totalAccountPnL.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            <sup className="theme-text-quaternary ml-1" style={{ fontSize: '0.5rem' }}>USD</sup>
          </span>
        </div>
        
        {/* Account Type Toggle */}
        <div className="flex items-center space-x-1 lg:space-x-2">
          <span className="hidden xl:block theme-text-quaternary text-xs">Account:</span>
          <span className="text-xs font-medium transition-colors duration-200 text-[#2AB7A6]">
            {isDemoAccount ? 'Demo' : 'Live'}
          </span>
          <button
            onClick={toggleAccountType}
            className={`relative inline-flex h-3 w-6 lg:h-4 lg:w-8 items-center rounded-full transition-colors duration-200 focus:outline-none ${
              isDemoAccount 
                ? theme === 'light' ? 'bg-gray-300' : 'bg-gray-500'
                : 'bg-[#2AB7A6]'
            }`}
          >
            <span
              className={`inline-block h-2 w-2 lg:h-3 lg:w-3 transform rounded-full bg-white transition-transform duration-200 ${
                isDemoAccount ? 'translate-x-0.5' : 'translate-x-3 lg:translate-x-4'
              }`}
            />
          </button>
        </div>
      </div>
      
      {/* Deposit Modal */}
      <DepositModal 
        isOpen={showDepositModal} 
        onClose={() => setShowDepositModal(false)} 
      />
    </div>
  );
});

HeaderMiddlePanel.displayName = 'HeaderMiddlePanel';

export default HeaderMiddlePanel;
